/**
 * 스피너 관리 모듈
 *
 * 사용법:
 * 1. 자동 스피너 (API 호출용) - axiosNew.js에서 자동 처리
 *    - 기본적으로 모든 API 호출 시 스피너 표시
 *    - 비활성화: { showSpinner: false } 옵션 사용
 *
 * 2. 수동 스피너 (컴포넌트용)
 *    import { manualSpinner } from '@/composables/spinner';
 *    manualSpinner.show();  // 스피너 표시
 *    manualSpinner.hide();  // 스피너 숨김
 *
 * 3. 기존 방식 호환
 *    import useSpinner from '@/composables/spinner';
 *    useSpinner(true);   // 스피너 표시
 *    useSpinner(false);  // 스피너 숨김
 */

let apiCount = 0; // API 호출 카운트 (자동 스피너용)
let manualCount = 0; // 수동 호출 카운트
let showTime = 0;
let hideTimeout = null;
let lockUntil = 0; // 잠금 해제 시간 (401 처리 후 깜빡임 방지)
const MIN_DISPLAY_TIME = 500; // 최소 표시 시간 (ms)
const LOCK_DURATION = 1000; // 401 후 스피너 잠금 시간 (ms)

// DOM에 스피너 추가/제거
const SpinnerDOM = {
  show: () => {
    if (document.querySelector('.momentum-container')) return;

    const container = document.createElement('div');
    const spinner = document.createElement('div');
    container.classList.add('momentum-container');
    spinner.classList.add('momentum');
    container.appendChild(spinner);

    document.body.appendChild(container);
    showTime = Date.now();
  },

  hide: () => {
    const removeDiv = document.querySelector('.momentum-container');
    if (removeDiv) {
      removeDiv.remove();
    }
  },

  // 최소 표시 시간을 고려한 숨김
  hideWithDelay: (callback) => {
    const elapsed = Date.now() - showTime;
    const remaining = MIN_DISPLAY_TIME - elapsed;

    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }

    if (remaining > 0) {
      hideTimeout = setTimeout(() => {
        SpinnerDOM.hide();
        hideTimeout = null;
        if (callback) callback();
      }, remaining);
    } else {
      SpinnerDOM.hide();
      if (callback) callback();
    }
  },
};

/**
 * 자동 스피너 (API 호출용)
 * axiosNew.js에서 사용
 */
export const autoSpinner = {
  start: () => {
    // 잠금 상태면 무시 (401 처리 후 깜빡임 방지)
    if (Date.now() < lockUntil) return;

    // 새 요청 시작 시 예약된 숨김 타이머 취소 (진행 중 숨김 방지)
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }

    apiCount += 1;
    if (apiCount === 1 && manualCount === 0) {
      SpinnerDOM.show();
    }
  },

  stop: () => {
    // 잠금 상태면 무시
    if (Date.now() < lockUntil) return;

    apiCount = Math.max(0, apiCount - 1);
    if (apiCount === 0 && manualCount === 0) {
      SpinnerDOM.hideWithDelay();
    }
  },

  /**
   * 강제 종료 (401 등 전역 에러 시)
   * 모든 카운트를 초기화하고 즉시 스피너 숨김
   * 일정 시간 동안 스피너 잠금하여 깜빡임 방지
   */
  forceStop: () => {
    apiCount = 0;
    manualCount = 0;
    lockUntil = Date.now() + LOCK_DURATION;
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    SpinnerDOM.hide();
  },
};

/**
 * 수동 스피너 (컴포넌트용)
 * 특정 작업에서 직접 스피너를 제어할 때 사용
 */
export const manualSpinner = {
  show: () => {
    // 새 수동 스피너 시작 시 예약된 숨김 타이머 취소
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }

    manualCount += 1;
    if (manualCount === 1 && apiCount === 0) {
      SpinnerDOM.show();
    }
  },

  hide: () => {
    manualCount = Math.max(0, manualCount - 1);
    if (manualCount === 0 && apiCount === 0) {
      SpinnerDOM.hideWithDelay();
    }
  },
};

/**
 * 기존 방식 호환용
 * @param {boolean} isLoading - true: 표시, false: 숨김
 * @deprecated autoSpinner 또는 manualSpinner 사용 권장
 */
const useSpinner = (isLoading) => {
  if (isLoading) {
    autoSpinner.start();
  } else {
    autoSpinner.stop();
  }
};

export default useSpinner;
